import { Box, Divider, Grid, Typography } from "@mui/joy";
import { ArrowLeft, UserRoundPlus } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

import { Button, TextField, Alert } from "../../../../shared";
import { newUserValidation } from "../../../../libs/form-validations";
import {
    useUser,
    useRoles,
    useOrganization,
    useUserRoles,
} from "../../../../hooks/query";
import { Breadcrumb } from "../../../../components";
import { useApiResponseParser, useAuth, useAuthorization } from "../../../../hooks";
import { PERMISSIONS } from "../../../../constants/permissions";
import { RoleSelectionList } from "../";

export default function NewUser() {
    const navigate = useNavigate();
    const { parseErrorResponseData } = useApiResponseParser();
    const { selectedOrganization } = useAuth();
    const { can } = useAuthorization();
    const canManage = can(PERMISSIONS.USERS_MANAGE);
    const { useCreateCognito } = useUser();
    const { useList: useListRoles } = useRoles();
    const { useAddUser } = useOrganization();
    const { useSync } = useUserRoles();

    const createCognitoSvc = useCreateCognito();
    const addUserToOrgSvc = useAddUser();
    const syncRolesSvc = useSync();
    const listRolesSvc = useListRoles();
    const [localError, setLocalError] = useState<string | null>(null);
    const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

    const rolesData = listRolesSvc.data || [];

    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            FirstName: "",
            LastName: "",
            Email: "",
            Password: "",
        },
        resolver: newUserValidation,
    });

    const {
        handleSubmit,
        formState: { isValid },
    } = methods;

    const registerUser = async (data: any) => {
        if (!canManage) return;
        setLocalError(null);

        if (!selectedOrganization?.Id) {
            setLocalError(
                "No active organization selected. Choose an organization from the sidebar first.",
            );
            return;
        }

        try {
            const response = await createCognitoSvc.mutateAsync({
                FirstName: data.FirstName,
                LastName: data.LastName,
                Email: data.Email,
                Password: data.Password,
                OrganizationId: selectedOrganization.Id,
            });

            const userId = response?.userId || response?.UserId;
            if (userId) {
                await addUserToOrgSvc.mutateAsync({
                    UserId: userId,
                    OrganizationId: selectedOrganization.Id,
                });

                if (selectedRoleIds.length) {
                    await syncRolesSvc.mutateAsync({
                        UserId: userId,
                        RoleIds: selectedRoleIds,
                        OrganizationId: selectedOrganization.Id,
                    });
                }
            }

            navigate(-1);
        } catch (e) {
            console.error("Failed to register user:", e);
        }
    };

    const isSaving =
        createCognitoSvc.isPending ||
        addUserToOrgSvc.isPending ||
        syncRolesSvc.isPending;
    const saveError =
        localError ||
        createCognitoSvc.error ||
        addUserToOrgSvc.error ||
        syncRolesSvc.error;

    if (!canManage) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Only Admin/Owner can create users." />
            </Box>
        );
    }

    return (
        <Box flex={1}>
            <Breadcrumb />
            <Divider />

            <Box p={3} pb={0} maxWidth={700}>
                <Typography level="title-lg">New User</Typography>
                <Typography level="body-sm">
                    Add a user to Cognito. They will be linked to{" "}
                    {selectedOrganization?.Name ? (
                        <Typography
                            component="span"
                            level="body-sm"
                            sx={{ fontWeight: 600 }}
                        >
                            {selectedOrganization.Name}
                        </Typography>
                    ) : (
                        "the active organization"
                    )}
                    .
                </Typography>
            </Box>

            <Box mt={2} ml={3} maxWidth={700} pb={4}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(registerUser)}>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6}>
                                    <TextField
                                        name="FirstName"
                                        label="First Name"
                                        helperText="User's first name"
                                        placeholder="e.g., Jane"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6}>
                                    <TextField
                                        name="LastName"
                                        label="Last Name"
                                        helperText="User's last name"
                                        placeholder="e.g., Doe"
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                name="Email"
                                label="Email"
                                type="email"
                                helperText="Primary email for login and communication"
                                placeholder="e.g., jane@example.com"
                            />

                            <TextField
                                name="Password"
                                label="Password"
                                type="password"
                                helperText="Optional. If empty, Cognito generates a temporary password."
                                placeholder="Optional temporary password"
                            />

                            <Box mt={0.5}>
                                <RoleSelectionList
                                    roles={rolesData}
                                    selectedRoleIds={selectedRoleIds}
                                    onChange={setSelectedRoleIds}
                                    loading={listRolesSvc.isLoading}
                                />
                            </Box>

                            <Box
                                mt={1}
                                display="flex"
                                alignItems="center"
                                gap={1.5}
                            >
                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    startDecorator={<ArrowLeft size={18} />}
                                    onClick={() => navigate(-1)}
                                    disabled={isSaving}
                                    sx={{ borderRadius: "4px" }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="soft"
                                    color="primary"
                                    startDecorator={<UserRoundPlus size={18} />}
                                    loading={isSaving}
                                    loadingPosition="start"
                                    type="submit"
                                    disabled={
                                        !isValid ||
                                        isSaving ||
                                        !selectedOrganization?.Id
                                    }
                                    sx={{ borderRadius: "4px" }}
                                >
                                    {isSaving ? "Creating..." : "Add User"}
                                </Button>
                            </Box>

                            {saveError ? (
                                <Alert
                                    errorMessage={
                                        typeof saveError === "string"
                                            ? saveError
                                            : parseErrorResponseData(saveError)
                                    }
                                />
                            ) : null}
                        </Box>
                    </form>
                </FormProvider>
            </Box>
        </Box>
    );
}
